export const getLocalizedText = (value, lang) => {
	if (value == null) return "";
	if (typeof value === "string") return value;
	if (typeof value === "number") return String(value);
	if (typeof value !== "object") return "";

	const candidates = [
		value[lang],
		value.en,
		value.ru,
		value.hy,
		...Object.values(value)
	];
	return candidates.find((candidate) => typeof candidate === "string" && candidate.trim()) || "";
};

export const buildCreateChatUserOptions = (users, currentUserId, lang, fallbackLabel) =>
	(Array.isArray(users) ? users : [])
		.map((user) => {
			const value = String(user?._id || "");
			const label =
				getLocalizedText(user?.name, lang) ||
				getLocalizedText(user?.fullName, lang) ||
				getLocalizedText(user?.username, lang) ||
				String(fallbackLabel || "");

			return { value, label };
		})
		.filter(
			(option) => option.value && option.value !== String(currentUserId || "")
		);

export const buildChatRoute = (baseRoute, chatId) => {
	const normalizedBaseRoute = String(baseRoute || "")
		.replace(/^\/+/, "")
		.replace(/\/+$/, "");
	const normalizedChatId = String(chatId || "")
		.replace(/^\/+/, "")
		.replace(/\/+$/, "");

	return `/${normalizedBaseRoute}/${normalizedChatId}`;
};

const getEntityId = (entity) => String(entity?._id || entity || "");

export const getDirectChatPartner = (chat, currentUserId) => {
	if (chat?.chatType !== "direct" || !Array.isArray(chat?.users)) return null;
	const normalizedCurrentUserId = String(currentUserId || "");
	return chat.users.find((participant) => getEntityId(participant) !== normalizedCurrentUserId) || null;
};

export const hasDirectChatWithUser = (chats, currentUserId, targetUserId) => {
	const normalizedTargetUserId = String(targetUserId || "");
	if (!normalizedTargetUserId) return false;
	return (Array.isArray(chats) ? chats : []).some((chat) => {
		const partner = getDirectChatPartner(chat, currentUserId);
		return getEntityId(partner) === normalizedTargetUserId;
	});
};

export const filterUserSearchResults = (users, searchQuery, currentUserId, existingChats, lang) => {
	const query = String(searchQuery || "").trim().toLowerCase();
	if (!query) return [];
	const normalizedCurrentUserId = String(currentUserId || "");

	return (Array.isArray(users) ? users : [])
		.filter((candidate) => {
			const candidateId = getEntityId(candidate);
			if (!candidateId || candidateId === normalizedCurrentUserId) return false;
			if (hasDirectChatWithUser(existingChats, currentUserId, candidateId)) return false;

			const searchableValues = [
				getLocalizedText(candidate?.name, lang),
				getLocalizedText(candidate?.fullName, lang),
				getLocalizedText(candidate?.username, lang),
				candidate?.username
			];
			return searchableValues.some((value) =>
				String(value || "").toLowerCase().includes(query)
			);
		})
		.slice(0, 20);
};

export const resolveDirectChatForUser = async ({
	API,
	chatGroupRoute,
	chatUserStateRoute,
	currentUserId,
	targetUserId
}) => {
	if (!API || !currentUserId || !targetUserId) return null;

	const existing = await API.get({
		route: chatGroupRoute,
		query: {
			chatType: "direct",
			users: { $all: [currentUserId, targetUserId], $size: 2 }
		},
		limit: 1
	});

	if (Array.isArray(existing) && existing[0]?._id) {
		if (chatUserStateRoute) {
			await API.update(chatUserStateRoute, existing[0]._id, { hidden: false });
		}
		return existing[0];
	}

	return API.post(chatGroupRoute, {
		users: [currentUserId, targetUserId],
		admins: [currentUserId],
		privateChat: true,
		readOnly: false,
		chatType: "direct",
		createdAt: new Date(),
		updated: new Date()
	});
};

export const groupChatsByType = (chats) => {
	const result = { direct: [], group: [], channel: [] };
	(Array.isArray(chats) ? chats : []).forEach((chat) => {
		const type =
			chat?.chatType === "direct"
				? "direct"
				: chat?.chatType === "channel"
					? "channel"
					: "group";
		result[type].push(chat);
	});
	return result;
};

/**
 * Split the sidebar chats into their rendered buckets.
 * Precedence: blocked (direct, needs currentUserId) > archived (any type) > pinned (any type) > by chatType.
 * Incoming order is preserved (server already sorts pinned-first by activity).
 */
export const isChatBlockedByUser = (chat, userId) =>
	chat?.chatType === "direct" &&
	(chat?.blockedUsers || []).some((blockedUserId) => String(blockedUserId) === String(userId));

export const partitionSidebarChats = (chats, currentUserId) => {
	const pinned = [];
	const archived = [];
	const blocked = [];
	const active = [];
	(Array.isArray(chats) ? chats : []).forEach((chat) => {
		if (currentUserId && isChatBlockedByUser(chat, currentUserId)) blocked.push(chat);
		else if (chat?.userState?.archived) archived.push(chat);
		else if (chat?.userState?.pinned) pinned.push(chat);
		else active.push(chat);
	});
	return { pinned, archived, blocked, ...groupChatsByType(active) };
};

/** A chat is muted while `muted` is set and any timed expiry is still in the future. */
export const isChatMuted = (userState, now = Date.now()) => {
	if (!userState?.muted) return false;
	if (!userState.mutedUntil) return true;
	return new Date(userState.mutedUntil).getTime() > now;
};

export const getUnreadCount = (chat) =>
	Number.isFinite(chat?.unreadCount)
		? Math.max(0, chat.unreadCount)
		: (Array.isArray(chat?.unreadMessages) ? chat.unreadMessages.length : 0);

/** Unread indicator: real unseen messages, or a manual "mark as unread" flag. */
export const hasUnread = (chat) =>
	getUnreadCount(chat) > 0 ||
	Boolean(chat?.userState?.manualUnread);

/** Unread messages, including muted chats; manual unread contributes at least one. */
export const getSidebarUnreadCount = (chats) =>
	chats.reduce((total, chat) => total + Math.max(getUnreadCount(chat), chat?.userState?.manualUnread ? 1 : 0), 0);

/** Unread chats first (stable), so pinned-then-activity order is only the tiebreaker. */
export const sortUnreadFirst = (chats) => [
	...chats.filter(hasUnread),
	...chats.filter((chat) => !hasUnread(chat))
];

/**
 * Every non-archived, non-blocked chat (any type), unread chats first,
 * preserving pinned/activity order within unread and read chats. Mirrors apit-mobile-chat's "All" folder.
 */
export const getAllSidebarChats = (chats, currentUserId) => {
	const list = Array.isArray(chats) ? chats : [];
	const pinned = new Set();
	const hidden = new Set();
	list.forEach((chat) => {
		if (currentUserId && isChatBlockedByUser(chat, currentUserId)) hidden.add(chat);
		else if (chat?.userState?.archived) hidden.add(chat);
		else if (chat?.userState?.pinned) pinned.add(chat);
	});
	const active = [
		...list.filter((chat) => pinned.has(chat)),
		...list.filter((chat) => !pinned.has(chat) && !hidden.has(chat))
	];
	return sortUnreadFirst(active);
};

export const filterSidebarChats = (chats, searchQuery, currentUserId, lang) => {
	const query = String(searchQuery || "").trim().toLowerCase();
	if (!query) return [];

	const normalizedCurrentUserId = String(currentUserId || "");

	return (Array.isArray(chats) ? chats : []).filter((chat) => {
		const users = Array.isArray(chat?.users) ? chat.users : [];
		const admins = Array.isArray(chat?.admins) ? chat.admins : [];

		if (
			chat?.privateChat &&
			normalizedCurrentUserId &&
			![...users, ...admins].some(
				(participant) => getEntityId(participant) === normalizedCurrentUserId
			)
		) {
			return false;
		}

		const searchableValues = [
			getLocalizedText(chat?.name, lang),
			...(Array.isArray(chat?.usernames) ? chat.usernames : [])
		];

		if (chat?.chatType === "direct") {
			const partner = getDirectChatPartner(chat, normalizedCurrentUserId);
			searchableValues.push(
				getLocalizedText(partner?.name, lang),
				getLocalizedText(partner?.fullName, lang),
				getLocalizedText(partner?.username, lang)
			);
		}

		return searchableValues.some((value) =>
			String(value || "").toLowerCase().includes(query)
		);
	});
};
